import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtUSD, safe } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Building2, MapPin, Trash2 } from "lucide-react";
import { CanvasTabs } from "@/components/meridian/CanvasTabs";
import { LoanDashboard } from "./LoanDashboard";
import { LoanEvidence } from "./LoanEvidence";
import { MemoDraft } from "./MemoDraft";
import { Assumptions } from "./Assumptions";
import { Forecast } from "./Forecast";
import { Recommendation } from "./Recommendation";
import { AuditPanel } from "./AuditPanel";
import { TenantStress } from "./TenantStress";
import { CompsMarket } from "./CompsMarket";
import { Committee } from "./Committee";
import { DecisionReport } from "./DecisionReport";
import RentRollTab from "./RentRollTab";

const REC = { Approve: "text-emerald-700", "Approve with conditions": "text-amber-700", Decline: "text-red-700" };

function DeleteLoan({ task }) {
  const nav = useNavigate();
  const { refreshTasks, persona } = useApp();
  const del = safe(async () => {
    if (!window.confirm(`Delete ${task.title} and its uploaded documents? This is logged.`)) return;
    await api.delete(`/tasks/${task.id}`);
    toast.success(`${task.title} deleted`); await refreshTasks(); nav("/lending");
  });
  if (!persona?.permissions.includes("lending.edit")) return null;
  return <button data-testid="delete-loan-button" onClick={del} title="Delete this loan file" className="text-slate-400 hover:text-red-600 shrink-0"><Trash2 className="w-4 h-4" /></button>;
}

export default function LoanCanvas({ task, tab, setTab, reload, pendingMemo, onMemoStarted, version }) {
  const L = task.loan;
  const r = task.recommendation;
  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="loan-canvas">
      <div className="flex items-center gap-4 px-6 py-4 border-b border-slate-200">
        {L.image ? <img src={L.image} alt={L.name} className="w-16 h-16 object-cover rounded border border-slate-300" /> : <div className="w-16 h-16 rounded-xl bg-sky-50 border border-sky-200 flex items-center justify-center" data-testid="loan-custom-icon"><Building2 className="w-7 h-7 text-sky-600" /></div>}
        <div className="min-w-0 flex-1">
          <div className="eyebrow">{L.purpose} · {L.property_type}</div>
          <h1 data-testid="loan-title" className="font-display text-2xl font-extrabold tracking-tight text-slate-900 truncate">{L.name}</h1>
          <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{L.location} · {L.borrower}{task.batch_id && <Link data-testid="loan-batch-link" to={`/lending/batch/${task.batch_id}`} className="ml-2 text-sky-700 hover:underline">From {task.batch_id}</Link>}</div>
        </div>
        <div className="text-right hidden lg:block">
          <div className="eyebrow">Requested</div>
          <div className="num text-xl text-slate-900">{fmtUSD(L.requested_amount, true)}</div>
          <div data-testid="loan-status" className="text-[11px] text-sky-700 mt-0.5">{task.status}</div>
          {r?.available && <div data-testid="loan-header-recommendation" className={`text-[11px] mt-0.5 ${REC[r.decision]}`}>System: {r.decision}</div>}
        </div>
        {task.custom && !task.decisions?.length && <DeleteLoan task={task} />}
      </div>
      <CanvasTabs tab={tab} setTab={setTab} tabs={[["dashboard", "Dashboard"], ["evidence", "Evidence", task.documents.length], ["rentroll", "Rent roll"], ["assumptions", "Assumptions"], ["forecast", "Forecast"], ["stress", "Tenant stress"], ["comps", "Comps & market"], ["recommendation", "Recommendation"], ["committee", "Committee"], ["decision", "Decision report"], ["draft", "Draft memo"], ["audit", "Audit"]]} />
      <div className="flex-1 min-h-0 overflow-y-auto thin-scroll">
        {tab === "dashboard" && <LoanDashboard task={task} reload={reload} setTab={setTab} />}
        {tab === "evidence" && <LoanEvidence task={task} reload={reload} />}
        {tab === "rentroll" && <RentRollTab task={task} reload={reload} />}
        {tab === "assumptions" && <Assumptions task={task} reload={reload} />}
        {tab === "forecast" && <Forecast task={task} reload={reload} />}
        {tab === "recommendation" && <Recommendation task={task} reload={reload} />}
        {tab === "draft" && <MemoDraft task={task} autoGenerate={pendingMemo} onStarted={onMemoStarted} reload={reload} />}
        {tab === "stress" && <TenantStress task={task} />}
        {tab === "comps" && <CompsMarket task={task} />}
        {tab === "committee" && <Committee task={task} />}
        {tab === "decision" && <DecisionReport task={task} reload={reload} />}
        {tab === "audit" && <AuditPanel task={task} version={version} />}
      </div>
    </div>
  );
}
